import React, { useEffect, useState } from "react";
import { RiDownload2Line } from "react-icons/ri";
import * as XLSX from "xlsx"; // Import xlsx library
import { get } from "../../utility/fetch";
import Spinner from "../UI/Spinner";
import Paginated from "../layouts/MUIPagination";
import notification from "../../utility/notification";
import Receipt from "../layouts/Receipt";


function PaymentRecords() {
  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false); // New state for export loading
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(10);
  const [recordType, setRecordType] = useState("WalkIn");
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const formatDateForAPI = (dateString) => {
    if (!dateString) return "";
    return dateString;
  };

  const fetchPaymentRecords = async (page = 1) => {
    setLoading(true);
    try {
      const start = formatDateForAPI(startDate);
      const end = formatDateForAPI(endDate);

      let url = `/paymentrecord/list/true/${recordType}?pageNumber=${page}&pageSize=${pageSize}&startDate=${start}&endDate=${end}`;
      
      const res = await get(url);

      if (res?.statusCode === 200) {
        setPaymentData(res?.data?.recordList || []);
        setTotalItems(res?.data?.metadata?.totalItems || 0);
        setCurrentPage(page);
      } else {
        notification({ message: "Failed to fetch payment records", type: "error" });
      }
    } catch (error) {
      console.error("Error fetching payment records:", error);
      notification({ message: "Error fetching payment records", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentRecords(1);
  }, [recordType, startDate, endDate]);

  const handlePageChange = (newPage) => {
    fetchPaymentRecords(newPage);
  };

  const handlePrintClick = (record) => {
    setSelectedRecord(record);
    setShowReceiptModal(true);
  };

  // --- EXPORT FUNCTIONALITY START ---
  const handleExport = async () => {
    if (totalItems === 0) {
      notification({ message: "No records to export", type: "warning" });
      return;
    }

    setExporting(true);
    try {
      const start = formatDateForAPI(startDate);
      const end = formatDateForAPI(endDate);
      
      // Fetch ALL records for the selected range by requesting a large pageSize
      // Alternatively, if your backend supports a specific export endpoint, use that.
      const largePageSize = 100000; 
      let url = `/paymentrecord/list/true/${recordType}?pageNumber=1&pageSize=${largePageSize}&startDate=${start}&endDate=${end}`;

      const res = await get(url);

      if (res?.statusCode === 200) {
        const records = res?.data?.recordList || [];

        // Format data for Excel
        const excelData = records.map((record) => {
          const patientName = `${record?.walkInLabRequest?.walkInPatient?.firstName || ''} ${record?.walkInLabRequest?.walkInPatient?.lastName || ''}`.trim();
          
          const totalAmount = record?.paymentRecordBreakdowns 
            ? record?.paymentRecordBreakdowns?.reduce((acc, curr) => acc + curr?.patientDeposit, 0) 
            : 0;
          
          const paymentDate = record?.paymentRecordBreakdowns && record?.paymentRecordBreakdowns.length > 0
            ? new Date(record?.paymentRecordBreakdowns[0]?.paymentDate).toLocaleDateString()
            : "N/A";

          const services = record?.paymentRecordBreakdowns 
            ? record?.paymentRecordBreakdowns?.map((item) => item?.internalLabService?.name).join(", ") 
            : "N/A";

          return {
            "Payment ID": record?.id,
            "Patient Name": patientName || "N/A",
            "Total Amount": totalAmount,
            "Payment Date": paymentDate,
            "Services": services,
            // "Status": record?.status || "Pending" // Uncomment if you want status
          };
        });

        // Create Worksheet
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Customize column widths (optional but makes it look nicer)
        const wscols = [
          { wch: 10 }, // ID
          { wch: 25 }, // Name
          { wch: 15 }, // Amount
          { wch: 15 }, // Date
          { wch: 50 }, // Services
        ];
        worksheet['!cols'] = wscols;

        // Create Workbook and append sheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Payment Records");

        // Generate file name with current date
        const fileName = `Payment_Records_${new Date().toISOString().split('T')[0]}.xlsx`;

        // Trigger Download
        XLSX.writeFile(workbook, fileName);
        
        notification({ message: "Export successful", type: "success" });
      } else {
        notification({ message: "Failed to fetch data for export", type: "error" });
      }
    } catch (error) {
      console.error("Export error:", error);
      notification({ message: "An error occurred during export", type: "error" });
    } finally {
      setExporting(false);
    }
  };
  // --- EXPORT FUNCTIONALITY END ---

  return (
    <div style={{ width: "100%", marginTop: "40px" }}>
      {showReceiptModal && selectedRecord && (
          <Receipt data={selectedRecord} onClose={() => setShowReceiptModal(false)} />
      )}


      <h1 style={{ marginTop: "20px" }}>Payment Records</h1>

      {/* Filter Form */}
      <div style={{ 
        backgroundColor: "#fff", 
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "20px",
        marginTop: "20px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px', 
          alignItems: 'flex-end' 
        }}>
          
          {/* Record Type Filter */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ marginBottom: "8px", fontWeight: "600", fontSize: "14px", color: "#333" }}>
              Record Type
            </label>
            <select
              value={recordType}
              onChange={(e) => {
                setRecordType(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "inherit",
                backgroundColor: "#fff",
                cursor: "pointer"
              }}
            >
              <option value="All">All</option>
              <option value="WalkIn">Walk-In</option>
              <option value="Referral">Referral</option>
            </select>
          </div>

          {/* Start Date */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ marginBottom: "8px", fontWeight: "600", fontSize: "14px", color: "#333" }}>
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "inherit",
                backgroundColor: "#fff"
              }}
            />
          </div>

          {/* End Date */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ marginBottom: "8px", fontWeight: "600", fontSize: "14px", color: "#333" }}>
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "inherit",
                backgroundColor: "#fff"
              }}
            />
          </div>

          {/* Export Button */}
          <button
            onClick={handleExport}
            disabled={exporting || loading || totalItems === 0}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              height: "42px",
              width: "100%",
              backgroundColor: exporting ? "#6c757d" : "#3C7E2D",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: (exporting || loading || totalItems === 0) ? "not-allowed" : "pointer",
              transition: "background-color 0.3s ease",
              opacity: (exporting || loading || totalItems === 0) ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!exporting && !loading && totalItems > 0) e.target.style.backgroundColor = "#2d5f21";
            }}
            onMouseLeave={(e) => {
              if (!exporting && !loading && totalItems > 0) e.target.style.backgroundColor = "#3C7E2D";
            }}
          >
            {exporting ? (
              <>
                <Spinner width="16px" height="16px" color="#fff" /> 
                Exporting...
              </>
            ) : (
              <>
                <RiDownload2Line size={18} />
                Export to Excel
              </>
            )}
          </button>
        </div>
      </div>

      {/* Payment Records Table */}
      <div style={{ width: "100%", marginTop: "40px" }}>
        {loading ? (
          <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: "40px" }}>
            <Spinner />
          </div>
        ) : (
          <>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
              backgroundColor: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              overflow: "hidden"
            }}>
              <thead style={{ backgroundColor: "#f5f5f5", borderBottom: "2px solid #3C7E2D" }}>
                <tr>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "14px", color: "#333" }}>Payment ID</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "14px", color: "#333" }}>Patient Name</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "14px", color: "#333" }}>Amount</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "14px", color: "#333" }}>Payment Date</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "14px", color: "#333" }}>Service(s)</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "14px", color: "#333" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(paymentData) && paymentData.length > 0 ? (
                  paymentData.map((record, index) => (
                    <tr key={record?.id || index} style={{ borderBottom: "1px solid #e0e0e0" }}>
                      <td style={{ padding: "12px", textAlign: "center", fontSize: "14px" }}>{record?.id}</td>
                      <td style={{ padding: "12px", textAlign: "center", fontSize: "14px" }}>{record?.walkInLabRequest?.walkInPatient?.firstName + " " + record?.walkInLabRequest?.walkInPatient?.lastName || "N/A"}</td>
                      <td style={{ padding: "12px", textAlign: "center", fontSize: "14px", fontWeight: "600", color: "#3C7E2D" }}>
                        {record?.paymentRecordBreakdowns ? record?.paymentRecordBreakdowns?.reduce((acc, currentValue) => acc + currentValue?.patientDeposit, 0) : "N/A"}
                      </td>
                      <td style={{ padding: "12px", textAlign: "center", fontSize: "14px" }}>
                        {record?.paymentRecordBreakdowns && record?.paymentRecordBreakdowns.length > 0
                          ? new Date(record?.paymentRecordBreakdowns[0]?.paymentDate).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td style={{ padding: "12px", textAlign: "center", fontSize: "14px" }}>{record?.paymentRecordBreakdowns ? record?.paymentRecordBreakdowns?.map((item) => <p key={item?.id}>{item?.internalLabService?.name}</p>) : "N/A"}</td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <button
                          onClick={() => handlePrintClick(record)}
                          style={{
                            backgroundColor: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            padding: "8px 12px",
                            cursor: "pointer"
                          }}
                        >Print</button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ padding: "20px", textAlign: "center", fontSize: "14px", color: "#999" }}>
                      No payment records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {paymentData.length > 0 && (
              <div style={{ marginTop: "20px" }}>
                <Paginated
                  count={totalItems}
                  page={currentPage}
                  onPageChange={handlePageChange}
                  pageSize={pageSize}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PaymentRecords;